// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import clsx from 'clsx';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useTranslation } from 'react-i18next';

import { logTraceEvent, sessionId } from './../../lib/ApplicationInsights';
import CorrelationVector from './../../lib/CorrelationVector';

import Styles from './errorMessage.module.scss';

export interface ErrorMessageProps {
    className?: object | string;
    contactSupport?: boolean;
    correlationVector?: string;
    message?: string;
    prependMessage?: () => JSX.Element | null;
    somethingWentWrong?: boolean;
    traceData?: any;
}

// ALWAYS keep this component mounted to the DOM so assistive technologies can announce dynamic changes to the content
// The error message will NOT be read on initial mount, it has to be dynamically modified
export class ErrorMessage extends React.Component<ErrorMessageProps> {
    componentDidMount() {
        const {
            contactSupport,
            correlationVector,
            message,
            somethingWentWrong,
            traceData,
        } = this.props;
        if (contactSupport || somethingWentWrong || message) {
            logTraceEvent(message || 'Something went wrong', {
                sessionId,
                traceData: JSON.stringify(traceData || {}).substring(0, 4096),
                traceDataMessage: traceData && traceData.message && JSON.stringify(traceData.message).substring(0, 256),
                correlationVector: correlationVector || CorrelationVector.getPreviousCV(),
            }, SeverityLevel.Error);
        }
    }

    componentDidUpdate(prevProps: ErrorMessageProps) {
        const {
            contactSupport,
            correlationVector,
            message,
            somethingWentWrong,
            traceData,
        } = this.props;
        if (
            // If choosing to show contactSupport when previously it was off
            ((contactSupport || somethingWentWrong) && !(prevProps.contactSupport || prevProps.somethingWentWrong))
            // Or if the message changed
            || (message !== prevProps.message)) {
            logTraceEvent(message || 'Something went wrong', {
                sessionId,
                correlationVector: correlationVector || CorrelationVector.getPreviousCV(),
                traceData: JSON.stringify(traceData || {}).substring(0, 4096),
                traceDataMessage: traceData && traceData.message && JSON.stringify(traceData.message).substring(0, 256),
            }, SeverityLevel.Error);
        }
    }

    render() {
        const {
            className,
            contactSupport,
            correlationVector,
            message,
            prependMessage,
            somethingWentWrong,
        } = this.props;

        return (
            // Using tabIndex on the error so the error div can be tabbed over and read
            <div tabIndex={message || contactSupport || somethingWentWrong ? 0 : -1} className={clsx(Styles.main, className)}
                role="status" aria-atomic="true" aria-live="assertive">
                {
                    message
                        ? (
                            <p>
                                {prependMessage ? prependMessage() : null}
                                {ReactHtmlParser(message)}
                            </p>
                        ) : null
                }

                <ContactSupport {...this.props} operationId={correlationVector || CorrelationVector.getPreviousCV()} />
            </div>
        );
    }
}

interface ContactSupportProps {
    contactSupport?: boolean;
    operationId?: string;
    sessionId?: string;
    somethingWentWrong?: boolean;
}

function ContactSupport(props: ContactSupportProps) {
    const { t } = useTranslation();
    const {
        contactSupport,
        operationId,
        somethingWentWrong,
    } = props;

    return contactSupport || somethingWentWrong
        ? (
            <p>
                {t(contactSupport ? 'please_contact_support' : 'something_went_wrong', { operationId, sessionId })}
            </p>
        ) : null;
}

export default ErrorMessage;
